import { Controller } from "@hotwired/stimulus"

// "On this page" outline for the story's left rail. Built from the rendered h2/h3s, shown only when
// there are at least two, with a marker that slides to the section currently being read.
//
// Headings get an id if they lack one so sections can be linked. Attributes add no text nodes,
// so highlight offsets (text_selection_controller) are unaffected.
export default class extends Controller {
  static targets = ["nav", "list", "body"]

  connect() {
    if (!this.hasBodyTarget || !this.hasListTarget) return
    this.headings = [...this.bodyTarget.querySelectorAll("h2, h3")].filter((h) => h.textContent.trim())
    if (this.headings.length < 2) return

    const used = new Set()
    this.items = this.headings.map((heading) => {
      if (!heading.id) heading.id = this.uniqueSlug(heading.textContent, used)
      used.add(heading.id)
      const li = document.createElement("li")
      li.className = `story-toc__item story-toc__item--${heading.tagName.toLowerCase()}`
      const a = document.createElement("a")
      a.href = `#${heading.id}`
      a.textContent = heading.textContent.trim()
      a.addEventListener("click", (event) => this.jump(event, heading))
      li.appendChild(a)
      this.listTarget.appendChild(li)
      return li
    })
    this.navTarget.hidden = false

    this.onScroll = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        this.updateActive()
      })
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })
    this.updateActive()
  }

  disconnect() {
    if (this.onScroll) {
      window.removeEventListener("scroll", this.onScroll)
      window.removeEventListener("resize", this.onScroll)
    }
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  jump(event, heading) {
    event.preventDefault()
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    heading.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" })
    history.replaceState(history.state, "", `#${heading.id}`)
  }

  updateActive() {
    const line = Math.min(160, window.innerHeight * 0.25)
    let index = -1
    this.headings.forEach((h, i) => {
      if (h.getBoundingClientRect().top <= line) index = i
    })
    if (index === this.activeIndex) return
    this.activeIndex = index

    this.items.forEach((li, i) => li.classList.toggle("is-active", i === index))
    const list = this.listTarget
    if (index < 0) {
      list.style.setProperty("--toc-on", "0")
      return
    }
    const li = this.items[index]
    list.style.setProperty("--toc-y", `${li.offsetTop}px`)
    list.style.setProperty("--toc-h", `${li.offsetHeight}px`)
    list.style.setProperty("--toc-on", "1")
  }

  uniqueSlug(text, used) {
    const base = text.toLowerCase().trim().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "section"
    let slug = base
    let n = 2
    while (used.has(slug) || document.getElementById(slug)) slug = `${base}-${n++}`
    return slug
  }
}
